import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { OnboardingStatusBanner, PortfolioPhotosCard, RevocationCard } from "@/components/modelform/ProfileMediaCards";
import { VerificationCard } from "@/components/modelform/VerificationCard";
import { BasicsCard, FeaturesCard, PermissionsCard, SkillsRatesCard, TosCard } from "@/components/modelform/ProfileFormSections";
import { ArrowLeft, Loader2 } from "lucide-react";

const EMPTY_FORM = {
  name: "", bio: "", gender: "",
  features: { hair_color: "", eye_color: "", skin_tone: "", height_cm: "" },
  measurements: { bust_cm: "", waist_cm: "", hips_cm: "", shoe_size_eu: "", dress_size: "" },
  categories: [], skills: [], interests: [], banned_categories: [],
  agency_commission_pct: "",
  rate_min: "", rate_max: "", available: true,
};

export default function ModelForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const fileRef = useRef(null);
  const selfieRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [model, setModel] = useState(null);
  const [tosAccepted, setTosAccepted] = useState(false);
  const [confirmed18, setConfirmed18] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => {
    if (isEdit) {
      api.get(`/models/${id}`).then(({ data }) => {
        setModel(data);
        setForm({
          name: data.name || "", bio: data.bio || "", gender: data.gender || "",
          features: {
            hair_color: data.features?.hair_color || "", eye_color: data.features?.eye_color || "",
            skin_tone: data.features?.skin_tone || "", height_cm: data.features?.height_cm ?? "",
          },
          measurements: {
            bust_cm: data.measurements?.bust_cm ?? "", waist_cm: data.measurements?.waist_cm ?? "",
            hips_cm: data.measurements?.hips_cm ?? "", shoe_size_eu: data.measurements?.shoe_size_eu ?? "",
            dress_size: data.measurements?.dress_size || "",
          },
          categories: data.categories || [], skills: data.skills || [], interests: data.interests || [],
          banned_categories: data.banned_categories || [],
          agency_commission_pct: data.agency_commission_pct ?? "",
          rate_min: data.rate_min ?? "", rate_max: data.rate_max ?? "", available: data.available ?? true,
        });
      }).catch(() => toast.error("Model not found"));
    }
  }, [id, isEdit]);

  const toPayload = () => ({
    name: form.name,
    bio: form.bio,
    gender: form.gender,
    features: {
      hair_color: form.features.hair_color,
      eye_color: form.features.eye_color,
      skin_tone: form.features.skin_tone,
      height_cm: form.features.height_cm ? parseInt(form.features.height_cm) : null,
    },
    measurements: {
      bust_cm: form.measurements.bust_cm ? parseInt(form.measurements.bust_cm) : null,
      waist_cm: form.measurements.waist_cm ? parseInt(form.measurements.waist_cm) : null,
      hips_cm: form.measurements.hips_cm ? parseInt(form.measurements.hips_cm) : null,
      shoe_size_eu: form.measurements.shoe_size_eu ? parseFloat(form.measurements.shoe_size_eu) : null,
      dress_size: form.measurements.dress_size,
    },
    categories: form.categories,
    skills: form.skills,
    interests: form.interests,
    banned_categories: form.banned_categories,
    agency_commission_pct: form.agency_commission_pct !== "" ? parseFloat(form.agency_commission_pct) : null,
    rate_min: form.rate_min ? parseFloat(form.rate_min) : null,
    rate_max: form.rate_max ? parseFloat(form.rate_max) : null,
    available: form.available,
    tos_accepted: isEdit ? true : tosAccepted,
    confirmed_18_plus: isEdit ? true : confirmed18,
  });

  const save = async (e) => {
    e.preventDefault();
    if (form.categories.length === 0) {
      toast.error("Pick at least one strength category");
      return;
    }
    if (!isEdit && (!tosAccepted || !confirmed18)) {
      toast.error("You must accept the Model Terms of Service and confirm age & photo ownership");
      return;
    }
    setBusy(true);
    try {
      if (isEdit) {
        const { data } = await api.put(`/models/${id}`, toPayload());
        setModel(data);
        toast.success("Profile updated");
      } else {
        const { data } = await api.post("/models", toPayload());
        toast.success("Profile created — complete onboarding to go live");
        navigate(`/models/${data.id}/onboarding`, { replace: true });
      }
    } catch (err) {
      toast.error(errMsg(err, "Could not save profile"));
    } finally {
      setBusy(false);
    }
  };

  const uploadFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      let updated = model;
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post(`/models/${id}/images`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        updated = data;
      }
      setModel(updated);
      toast.success(`${files.length} photo${files.length > 1 ? "s" : ""} uploaded`);
    } catch (err) {
      toast.error(errMsg(err, "Upload failed"));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeImage = async (imageId) => {
    try {
      const { data } = await api.delete(`/models/${id}/images/${imageId}`);
      setModel(data);
      toast.success("Photo removed");
    } catch (err) {
      toast.error(errMsg(err, "Could not remove photo"));
    }
  };

  const setPrimary = async (imageId) => {
    try {
      const { data } = await api.put(`/models/${id}/images/${imageId}/primary`);
      setModel(data);
      toast.success("Primary photo set");
    } catch (err) {
      toast.error(errMsg(err, "Could not set primary"));
    }
  };

  const uploadSelfie = async (files) => {
    if (!files?.length) return;
    setVerifying(true);
    try {
      const fd = new FormData();
      fd.append("file", files[0]);
      const { data } = await api.post(`/models/${id}/verify`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setModel(data);
      if (data.verification?.status === "verified") {
        toast.success("Identity verified - your profile now carries the Verified badge");
      } else {
        toast.error("Verification did not pass - see the result below and try another selfie");
      }
    } catch (err) {
      toast.error(errMsg(err, "Verification failed. Try again."));
    } finally {
      setVerifying(false);
      if (selfieRef.current) selfieRef.current.value = "";
    }
  };

  const toggleCategory = (cat) => {
    setForm((f) => ({
      ...f,
      categories: f.categories.includes(cat) ? f.categories.filter((c) => c !== cat) : [...f.categories, cat],
    }));
  };

  const toggleBanned = (cat) => {
    setForm((f) => ({
      ...f,
      banned_categories: f.banned_categories.includes(cat)
        ? f.banned_categories.filter((c) => c !== cat)
        : [...f.banned_categories, cat],
    }));
  };

  const revoke = async () => {
    try {
      await api.post(`/models/${id}/revoke`);
      toast.success("Likeness revoked - your profile is off the marketplace. Active licences finish their run.");
      navigate("/dashboard");
    } catch (err) {
      toast.error(errMsg(err, "Could not revoke"));
    }
  };

  if (!authLoading && (!user || !(["model", "agency"].includes(user.role)))) {
    return (
      <div className="page-container py-20 text-center">
        <p className="font-display text-2xl">Models & agencies only</p>
        <Button className="mt-4 rounded-xl" onClick={() => navigate("/auth")}>Sign in</Button>
      </div>
    );
  }

  return (
    <div className="page-container max-w-3xl py-8 sm:py-12">
      <button data-testid="model-form-back-button" onClick={() => navigate("/dashboard")} className="mb-6 flex items-center gap-1.5 text-sm text-muted-foreground transition-[color] hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Dashboard
      </button>
      <h1 className="font-display text-3xl tracking-[-0.01em]">{isEdit ? `Edit ${model?.name || "profile"}` : "Create model profile"}</h1>
      <p className="mt-1 text-sm text-muted-foreground">A rich profile with varied photos gets found and booked more often.</p>

      {isEdit && model && <OnboardingStatusBanner model={model} id={id} />}

      {isEdit && (
        <PortfolioPhotosCard
          model={model}
          uploading={uploading}
          fileRef={fileRef}
          onUpload={uploadFiles}
          onRemove={removeImage}
          onSetPrimary={setPrimary}
        />
      )}

      {isEdit && model && (
        <VerificationCard model={model} user={user} verifying={verifying} selfieRef={selfieRef} onUploadSelfie={uploadSelfie} />
      )}

      <form onSubmit={save} className="mt-6 space-y-6">
        <BasicsCard form={form} setForm={setForm} onToggleCategory={toggleCategory} />
        <FeaturesCard form={form} setForm={setForm} />
        <PermissionsCard form={form} user={user} onToggleBanned={toggleBanned} />
        <SkillsRatesCard form={form} setForm={setForm} user={user} />

        {!isEdit && (
          <TosCard
            user={user}
            tosAccepted={tosAccepted}
            setTosAccepted={setTosAccepted}
            confirmed18={confirmed18}
            setConfirmed18={setConfirmed18}
          />
        )}

        <Button data-testid="model-form-save-button" type="submit" disabled={busy} size="lg"
          className="rounded-xl shadow-[0_10px_25px_rgba(30,26,22,0.12)] transition-[box-shadow] hover:shadow-[0_14px_35px_rgba(30,26,22,0.16)]">
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {isEdit ? "Save changes" : "Create profile & add photos"}
        </Button>
      </form>

      {isEdit && model && !model.revoked && <RevocationCard model={model} onRevoke={revoke} />}
    </div>
  );
}
